import React, { useEffect, useRef, useState, useCallback } from 'react';

// --- Constants ---
const CANVAS_WIDTH = 600;
const CANVAS_HEIGHT = 600;
const MAX_ITERATIONS = 1000;
const DEFAULT_ZOOM = 250;
const DEFAULT_PAN_X = 0;
const DEFAULT_PAN_Y = 0;
const COLOR_SCHEMES = ['classic', 'rainbow', 'ocean', 'fire'];

const MandelbrotSet = () => {
  const canvasRef = useRef(null);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [panX, setPanX] = useState(DEFAULT_PAN_X);
  const [panY, setPanY] = useState(DEFAULT_PAN_Y);
  const [iterations, setIterations] = useState(MAX_ITERATIONS);
  const [colorScheme, setColorScheme] = useState('classic');
  const [isCalculating, setIsCalculating] = useState(false);
  const [smoothColoring, setSmoothColoring] = useState(true);
  const [quality, setQuality] = useState(1); // 1 = 고품질, 2 = 중간품질, 4 = 저품질

  // 마우스 드래그 상태 관리
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const initialPan = useRef({ x: 0, y: 0 });

  // 망델브로 집합 계산 함수
  const calculateMandelbrot = useCallback((x0, y0, maxIter) => {
    let x = 0;
    let y = 0;
    let x2 = 0;
    let y2 = 0;
    let iteration = 0;

    // 망델브로 반복: z_{n+1} = z_n^2 + c
    while (x2 + y2 <= 4 && iteration < maxIter) {
      y = 2 * x * y + y0;
      x = x2 - y2 + x0;
      x2 = x * x;
      y2 = y * y;
      iteration++;
    }

    // 발산하지 않는 경우 (망델브로 집합에 속함)
    if (iteration === maxIter) return -1;

    // 부드러운 색상 효과를 위한 계산
    if (smoothColoring) {
      // 로그 스케일 처리된 결과값 (더 부드러운 색상 전환)
      const logZn = Math.log(x2 + y2) / 2;
      const nu = Math.log(logZn / Math.log(2)) / Math.log(2);
      return iteration + 1 - nu;
    }
    
    return iteration;
  }, [smoothColoring]);

  // 색상 선택 함수
  const getColor = useCallback((iteration, maxIter) => {
    if (iteration === -1) return '#000000'; // 망델브로 집합 내부 (검정)

    // 색상 매핑에 사용할 정규화된 값 (0-1 사이)
    const t = iteration / maxIter;
    
    // 색상 스키마에 따른 처리
    switch (colorScheme) {
      case 'rainbow':
        // 무지개 색상 (HSL 색상환 회전)
        const hue = (iteration % 360) * 1.5;
        const lightness = 50;
        const saturation = 100 * (1 - t * 0.5);
        return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
        
      case 'ocean':
        // 바다 색상 (푸른색 계열)
        const r = Math.floor(Math.sin(t * 5) * 55 + 40);
        const g = Math.floor(Math.sin(t * 5 + 2) * 100 + 120);
        const b = Math.floor(Math.sin(t * 5 + 4) * 55 + 200);
        return `rgb(${r}, ${g}, ${b})`;
        
      case 'fire':
        // 불 색상 (붉은색/노란색 계열)
        const r2 = Math.floor(Math.sin(t * 3) * 35 + 220);
        const g2 = Math.floor(Math.sin(t * 3 + 2) * 100 + 100);
        const b2 = Math.floor(Math.sin(t * 3 + 4) * 15 + 15);
        return `rgb(${r2}, ${g2}, ${b2})`;
        
      default: // 'classic'
        // 클래식 망델브로 색상 (검정->파랑->보라->빨강->노랑->흰색)
        const v = 1 - t;
        const r3 = Math.floor(9 * (1 - v) * v * v * v * 255);
        const g3 = Math.floor(15 * (1 - v) * (1 - v) * v * v * 255);
        const b3 = Math.floor(8.5 * (1 - v) * (1 - v) * (1 - v) * v * 255);
        return `rgb(${r3}, ${g3}, ${b3})`;
    }
  }, [colorScheme]);

  // 망델브로 집합 렌더링 함수
  const renderMandelbrot = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const imageData = ctx.createImageData(CANVAS_WIDTH, CANVAS_HEIGHT);
    const data = imageData.data;

    setIsCalculating(true);

    // 각 픽셀에 대해 계산
    const centerX = CANVAS_WIDTH / 2;
    const centerY = CANVAS_HEIGHT / 2;

    // 웹 워커나 requestAnimationFrame 대신 직접 계산
    // 퍼포먼스를 위해 quality 설정에 따라 픽셀 건너뛰기
    for (let y = 0; y < CANVAS_HEIGHT; y += quality) {
      for (let x = 0; x < CANVAS_WIDTH; x += quality) {
        // 캔버스 좌표를 복소 평면 좌표로 변환
        const x0 = (x - centerX) / zoom - panX / zoom;
        const y0 = (y - centerY) / zoom - panY / zoom;
        
        // 망델브로 계산
        const iteration = calculateMandelbrot(x0, y0, iterations);
        
        // 색상 계산
        const color = getColor(iteration, iterations);
        
        // RGB 색상 파싱 (간단한 구현)
        let r, g, b;
        if (color.startsWith('#')) {
          // HEX 색상
          const hex = color.substring(1);
          r = parseInt(hex.substring(0, 2), 16);
          g = parseInt(hex.substring(2, 4), 16);
          b = parseInt(hex.substring(4, 6), 16);
        } else if (color.startsWith('rgb')) {
          // RGB 색상
          const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
          if (rgbMatch) {
            r = parseInt(rgbMatch[1]);
            g = parseInt(rgbMatch[2]);
            b = parseInt(rgbMatch[3]);
          } else {
            // HSL 색상일 경우 (복잡한 변환 생략)
            r = g = b = 0;
          }
        } else {
          // HSL 색상일 경우 (브라우저에 렌더링 맡기기)
          const tempDiv = document.createElement('div');
          tempDiv.style.color = color;
          document.body.appendChild(tempDiv);
          const computedColor = window.getComputedStyle(tempDiv).color;
          document.body.removeChild(tempDiv);
          
          const rgbMatch = computedColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
          if (rgbMatch) {
            r = parseInt(rgbMatch[1]);
            g = parseInt(rgbMatch[2]);
            b = parseInt(rgbMatch[3]);
          } else {
            r = g = b = 0;
          }
        }
        
        // quality 설정에 따라 여러 픽셀에 동일한 색상 적용
        for (let yy = 0; yy < quality && y + yy < CANVAS_HEIGHT; yy++) {
          for (let xx = 0; xx < quality && x + xx < CANVAS_WIDTH; xx++) {
            const pixelIndex = ((y + yy) * CANVAS_WIDTH + (x + xx)) * 4;
            data[pixelIndex] = r;     // R
            data[pixelIndex + 1] = g; // G
            data[pixelIndex + 2] = b; // B
            data[pixelIndex + 3] = 255; // Alpha (불투명)
          }
        }
      }
    }
    
    ctx.putImageData(imageData, 0, 0);
    setIsCalculating(false);
    
    // 수식 및 좌표 표시
    ctx.font = 'italic 14px Arial';
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.textAlign = 'right';
    ctx.fillText('z_{n+1} = z_n^2 + c', CANVAS_WIDTH - 20, CANVAS_HEIGHT - 50);
    ctx.fillText(`중심: (${(panX / zoom).toFixed(6)}, ${(panY / zoom).toFixed(6)}), 줌: ${zoom.toFixed(1)}`, 
                CANVAS_WIDTH - 20, CANVAS_HEIGHT - 30);
  }, [zoom, panX, panY, iterations, calculateMandelbrot, getColor, quality]);

  // 뷰포트 리셋
  const resetView = () => {
    setZoom(DEFAULT_ZOOM);
    setPanX(DEFAULT_PAN_X);
    setPanY(DEFAULT_PAN_Y);
    setColorScheme('classic');
    setQuality(1);
    setIterations(MAX_ITERATIONS);
    setSmoothColoring(true);
  };

  // 색상 스키마 변경
  const changeColorScheme = (scheme) => {
    setColorScheme(scheme);
  };

  // 마우스 이벤트 핸들러
  const handleMouseDown = (e) => {
    isDragging.current = true;
    dragStart.current = { x: e.clientX, y: e.clientY };
    initialPan.current = { x: panX, y: panY };
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    
    setPanX(initialPan.current.x - dx);
    setPanY(initialPan.current.y - dy);
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleWheel = (e) => {
    e.preventDefault();
    
    // 마우스 포인터 위치 계산 (캔버스 중심 기준)
    const rect = canvasRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left - CANVAS_WIDTH / 2;
    const mouseY = e.clientY - rect.top - CANVAS_HEIGHT / 2;
    
    // 줌 인/아웃 (마우스 휠 방향에 따라)
    const zoomFactor = e.deltaY < 0 ? 1.2 : 0.8;
    
    // 마우스 위치를 중심으로 줌 조정
    const newZoom = zoom * zoomFactor;
    const newPanX = panX + mouseX * (1 - zoomFactor);
    const newPanY = panY + mouseY * (1 - zoomFactor);
    
    setZoom(newZoom);
    setPanX(newPanX);
    setPanY(newPanY);
  };

  // 초기화 및 변경 시 다시 그리기
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    canvas.width = CANVAS_WIDTH;
    canvas.height = CANVAS_HEIGHT;
    
    // 캔버스 배경 색상
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#111';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    
    // 로딩 표시
    ctx.font = 'bold 20px Arial';
    ctx.fillStyle = '#FFF';
    ctx.textAlign = 'center';
    ctx.fillText('계산 중...', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2);
    
    // 비동기적으로 프랙탈 렌더링 (UI 블로킹 방지)
    setTimeout(() => {
      renderMandelbrot();
    }, 10);
    
    // 이벤트 리스너 등록
    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('mouseleave', handleMouseUp);
    canvas.addEventListener('wheel', handleWheel);
    
    // 컴포넌트 언마운트 시 이벤트 리스너 정리
    return () => {
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('mouseleave', handleMouseUp);
      canvas.removeEventListener('wheel', handleWheel);
    };
  }, [zoom, panX, panY, iterations, colorScheme, quality, renderMandelbrot]);

  // 흥미로운 위치 프리셋
  const presets = [
    { name: '전체 프랙탈', x: 0, y: 0, zoom: 250 },
    { name: '끝없는 나선', x: -0.743644786, y: 0.1318252536, zoom: 2000 },
    { name: '미니 망델브로', x: -0.1528, y: -1.0397, zoom: 1500 },
    { name: '시골의 모습', x: -0.5623, y: 0.6589, zoom: 800 },
    { name: '뿔 형태', x: -0.7454, y: 0.1130, zoom: 1500 }
  ];

  // 프리셋 적용 함수
  const applyPreset = (preset) => {
    setPanX(preset.x * zoom);
    setPanY(preset.y * zoom);
    setZoom(preset.zoom);
  };

  return (
    <div style={containerStyle}>
      <h2 style={titleStyle}>Mandelbrot Set Fractal</h2>
      <div style={contentContainerStyle}>
        <div style={canvasContainerStyle}>
          <canvas 
            ref={canvasRef} 
            width={CANVAS_WIDTH} 
            height={CANVAS_HEIGHT} 
            style={{
              ...canvasStyle,
              cursor: isDragging.current ? 'grabbing' : 'grab'
            }} 
          />
          {isCalculating && (
            <div style={loadingOverlayStyle}>
              <span>계산 중...</span>
            </div>
          )}
        </div>
        
        <div style={controlsContainerStyle}>
          <div style={controlGroupStyle}>
            <h3 style={subtitleStyle}>설정</h3>
            
            <div style={controlRowStyle}>
              <label style={labelStyle}>반복 횟수:</label>
              <input
                type="range"
                min="50"
                max="1000"
                step="50"
                value={iterations}
                onChange={(e) => setIterations(parseInt(e.target.value))}
                style={sliderStyle}
              />
              <span style={valueStyle}>{iterations}</span>
            </div>
            
            <div style={controlRowStyle}>
              <label style={labelStyle}>품질:</label>
              <select 
                value={quality} 
                onChange={(e) => setQuality(parseInt(e.target.value))}
                style={selectStyle}
              >
                <option value="1">높음 (고품질)</option>
                <option value="2">중간 (빠른 렌더링)</option>
                <option value="4">낮음 (최대 속도)</option>
              </select>
            </div>
            
            <div style={controlRowStyle}>
              <label style={labelStyle}>색상 방식:</label>
              <div style={colorButtonsContainerStyle}>
                {COLOR_SCHEMES.map(scheme => (
                  <button
                    key={scheme}
                    onClick={() => changeColorScheme(scheme)}
                    style={{
                      ...colorButtonStyle,
                      background: colorScheme === scheme 
                        ? getColorSchemeSample(scheme) 
                        : '#444',
                      color: 'white'
                    }}
                  >
                    {getSchemeDisplayName(scheme)}
                  </button>
                ))}
              </div>
            </div>
            
            <div style={controlRowStyle}>
              <label style={checkboxLabelStyle}>
                <input
                  type="checkbox"
                  checked={smoothColoring}
                  onChange={(e) => setSmoothColoring(e.target.checked)}
                  style={checkboxStyle}
                />
                부드러운 색상 전환
              </label>
            </div>
          </div>
          
          <div style={controlGroupStyle}>
            <h3 style={subtitleStyle}>탐색</h3>
            
            <div style={presetsContainerStyle}>
              {presets.map((preset, index) => (
                <button 
                  key={index}
                  onClick={() => applyPreset(preset)}
                  style={presetButtonStyle}
                >
                  {preset.name}
                </button>
              ))}
            </div>
            
            <p style={instructionStyle}>
              드래그: 이동 | 휠: 확대/축소
            </p>
            
            <button onClick={resetView} style={resetButtonStyle}>
              초기화
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// 색상 스키마 샘플 배경 생성
const getColorSchemeSample = (scheme) => {
  switch (scheme) {
    case 'rainbow':
      return 'linear-gradient(to right, violet, indigo, blue, green, yellow, orange, red)';
    case 'ocean':
      return 'linear-gradient(to right, #000033, #0066cc, #66ccff)';
    case 'fire':
      return 'linear-gradient(to right, #330000, #aa3300, #ff9900)';
    default: // 'classic'
      return 'linear-gradient(to right, #000000, #000055, #5500aa, #aa0055, #ffaa00)';
  }
};

// 색상 스키마 이름 표시
const getSchemeDisplayName = (scheme) => {
  switch (scheme) {
    case 'rainbow': return '무지개';
    case 'ocean': return '바다';
    case 'fire': return '불꽃';
    default: return '클래식';
  }
};

// --- Styled Components ---
const containerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem',
  width: '100%',
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #0a0a0f 0%, #0f1024 100%)',
  color: '#e0e0e0',
  fontFamily: 'Arial, sans-serif'
};

const titleStyle = {
  margin: '0 0 1.5rem 0',
  color: '#ffffff',
  fontSize: '2rem',
  textShadow: '0 2px 4px rgba(0,0,0,0.5)',
  letterSpacing: '1px'
};

const contentContainerStyle = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'center',
  gap: '2rem',
  flexWrap: 'wrap'
};

const canvasContainerStyle = {
  position: 'relative',
  padding: '0.5rem',
  background: 'rgba(0,0,0,0.6)',
  borderRadius: '12px',
  boxShadow: '0 10px 20px rgba(0,0,0,0.3), 0 6px 6px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.05) inset',
  overflow: 'hidden'
};

const canvasStyle = {
  display: 'block',
  borderRadius: '8px',
  boxShadow: '0 0 0 1px rgba(255,255,255,0.05) inset'
};

const loadingOverlayStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(0,0,0,0.7)',
  color: 'white',
  fontSize: '1.5rem',
  fontWeight: 'bold',
  borderRadius: '12px'
};

const controlsContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem',
  maxWidth: '350px',
  background: 'rgba(0,0,0,0.5)',
  borderRadius: '12px',
  padding: '1.5rem',
  boxShadow: '0 8px 16px rgba(0,0,0,0.3)'
};

const controlGroupStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem'
};

const subtitleStyle = {
  margin: '0 0 0.5rem 0',
  fontSize: '1.3rem',
  color: '#fff',
  borderBottom: '1px solid rgba(255,255,255,0.1)',
  paddingBottom: '0.5rem'
};

const controlRowStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  marginBottom: '0.8rem'
};

const labelStyle = {
  fontSize: '0.95rem',
  fontWeight: 'bold',
  color: '#ccc'
};

const sliderStyle = {
  width: '100%',
  height: '10px',
  appearance: 'none',
  background: 'linear-gradient(to right, #1a103f, #8e44ad)',
  outline: 'none',
  borderRadius: '10px',
  cursor: 'pointer'
};

const valueStyle = {
  fontSize: '0.8rem',
  color: '#aaa',
  alignSelf: 'flex-end'
};

const selectStyle = {
  padding: '0.5rem',
  borderRadius: '5px',
  background: '#333',
  color: 'white',
  border: '1px solid #666',
  outline: 'none',
  cursor: 'pointer',
  width: '100%'
};

const colorButtonsContainerStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.5rem'
};

const colorButtonStyle = {
  padding: '0.5rem',
  borderRadius: '5px',
  border: 'none',
  cursor: 'pointer',
  fontSize: '0.8rem',
  flex: '1 1 auto',
  textAlign: 'center',
  boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
  transition: 'all 0.2s ease'
};

const checkboxLabelStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  fontSize: '0.95rem',
  color: '#ccc',
  cursor: 'pointer'
};

const checkboxStyle = {
  width: '16px',
  height: '16px',
  cursor: 'pointer'
};

const presetsContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem'
};

const presetButtonStyle = {
  padding: '0.6rem',
  borderRadius: '5px',
  background: 'linear-gradient(135deg, #1b1464 0%, #2c3e50 100%)',
  color: 'white',
  border: 'none',
  cursor: 'pointer',
  fontSize: '0.9rem',
  textAlign: 'center',
  boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
  transition: 'all 0.2s ease'
};

const instructionStyle = {
  fontSize: '0.85rem',
  color: '#aaa',
  margin: '0.5rem 0',
  textAlign: 'center',
  fontStyle: 'italic'
};

const resetButtonStyle = {
  padding: '0.8rem',
  borderRadius: '8px',
  background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
  color: 'white',
  border: 'none',
  cursor: 'pointer',
  fontSize: '1rem',
  fontWeight: 'bold',
  width: '100%',
  marginTop: '0.5rem',
  boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
  transition: 'all 0.2s ease'
};

export default MandelbrotSet; 